import type { ConversationSummary } from '../../hooks/useDirectMessages';
import styles from './directMessages.module.scss';
import { formatThreadDate } from './dmFormat';

interface Props {
  conversations: ConversationSummary[];
  loading: boolean;
  activeConvId?: string;
  onSelect: (convId: string) => void;
}

/**
 * TRIL-394: the inbox — threads as links, each showing the other user, the date,
 * a preview, and an unread badge. Ordered newest-first by the server.
 */
export function ConversationList({
  conversations,
  loading,
  activeConvId,
  onSelect,
}: Props) {
  if (loading && conversations.length === 0) {
    return <p className={styles.hint}>Loading conversations…</p>;
  }
  if (conversations.length === 0) {
    return <p className={styles.hint}>No conversations yet.</p>;
  }

  return (
    <ul className={styles.threads}>
      {conversations.map((c) => {
        const name = c.otherUser?.username ?? 'Unknown user';
        const unread = c.unreadCount ?? 0;
        const isActive = c.convId === activeConvId;
        return (
          <li key={c.convId}>
            <button
              type="button"
              className={`${styles.threadLink} ${isActive ? styles.threadActive : ''}`}
              onClick={() => onSelect(c.convId)}
              aria-current={isActive ? 'true' : undefined}
            >
              <span className={styles.avatar} aria-hidden="true">
                {name.charAt(0).toUpperCase()}
              </span>
              <span className={styles.threadMeta}>
                <span className={styles.threadTop}>
                  <span className={styles.threadName}>{name}</span>
                  <span className={styles.threadDate}>
                    {formatThreadDate(c.lastMessageAt)}
                  </span>
                </span>
                <span className={styles.threadPreview}>{c.preview ?? ''}</span>
              </span>
              {unread > 0 && (
                <span
                  className={styles.unread}
                  role="img"
                  aria-label={`${unread} unread`}
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
